import { useCallback, useState } from "react";
import type { Failure } from "@/modules/shared/failure";
import type { FormState } from "@/modules/shared/forms";
import type { ApiResult } from "./api";

/**
 * Submitting a form, on a client that has no form actions (R22 T11).
 *
 * **This is `useActionState` and `useFormStatus` put back together by hand**,
 * and it is the one shape the Next host gave us for free. Over there a form
 * posted to a server action, React tracked pending, and the action returned a
 * `FormState`. Here there is no action to post to, so a screen calls the API
 * itself; what must not change is the *result*, because `FormState` is what the
 * screen docs are written against and what every `Field` renders.
 *
 * So the failure never becomes a sentence here. It is handed to a copy map
 * under `modules/*` (`formStateFor`, `passwordChangeStateFor`), the same pure,
 * unit-tested map the server adapter used. A wording change stays a one-file
 * change even while two frontends are live.
 */
export interface Form {
  /** What the screen renders: banner, per-field errors, echoed values. */
  readonly state: FormState;
  /** True between submit and answer. Disables the fieldset, swaps the label. */
  readonly pending: boolean;
  /**
   * Run one command. Returns its value on success and `null` on refusal,
   * having already put the refusal on screen.
   */
  readonly submit: <T>(
    call: () => Promise<ApiResult<T>>,
    render: (failure: Failure, values: Record<string, string>) => FormState,
    values?: Record<string, string>,
  ) => Promise<T | null>;
  /** For the few screens that set a banner of their own (a resend, a hint). */
  readonly setState: (state: FormState) => void;
}

export function useForm(): Form {
  const [state, setState] = useState<FormState>({});
  const [pending, setPending] = useState(false);

  const submit = useCallback(
    async <T,>(
      call: () => Promise<ApiResult<T>>,
      render: (failure: Failure, values: Record<string, string>) => FormState,
      values: Record<string, string> = {},
    ): Promise<T | null> => {
      // Clearing first matters: a second attempt that fails the same way should
      // still feel like it happened, and a stale banner sitting through a
      // pending state reads as if nothing was sent.
      setState({});
      setPending(true);
      const result = await call();
      setPending(false);

      if (!result.ok) {
        setState(render(result.failure, values));
        return null;
      }
      return result.value;
    },
    [],
  );

  return { state, pending, submit, setState };
}
